import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  Button,
  Grid,
  Paper,
  FormControl,
  FormHelperText,
  TextField,
  TextareaAutosize,
  makeStyles,
  useMediaQuery,
  createTheme,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  validateEmailAddress,
  validateStandard,
} from "../shared/util/UserValidations";
import EmailIcon from "../assets/images/svg/envelope.svg";
import PhoneIcon from "../assets/images/svg/phone.svg";
import { SiteConfigContext } from "../SiteConfigContext";
import axios from 'axios';


function ContactBlock(props) {
  const contactConfig = props.page.components.find(
    (component) => component.block_type === "ContactBlock"
  );
  const components = props.page.components.filter(
    (component) =>  {return component.block_type !== "ContactBlock" && component.block_type !== "MetaDataBlock"}
  );

  const siteConfig = useContext(SiteConfigContext);
  const retailerDpnInstance = siteConfig[0].retailer_dpn_instance;
  const dpnInstance = siteConfig[0].dpn_instance;
  const [captchaError, setCaptchaError] = useState('');
  const { user } = useSelector((state) => state);
  const cachedUser = JSON.parse(localStorage.getItem("userData"));
  const isLogin = !!user;

  const [values, setValues] = useState({
    email: "",
    name: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    emailError: "",
    firstNameError: "",
    lastNameError: "",
    messageError: "",
    generalError: "",
  });
  const [validations, setValidations] = useState({
    isLoading: false,
    successMessage: "",
  });
  const navigate = useNavigate();
  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [captchaVerified, setCaptchaVerified] = useState(false);
    const captchaRef = useRef(null);
    const handleCaptchaSuccess = (token) => {
      setCaptchaError('');
      setCaptchaVerified(true);
    };
  
    const handleCaptchaError = () => {
      setCaptchaVerified(false);
    };

    const resetCaptcha = () => {
      if (captchaRef.current && window.turnstile) {
        window.turnstile.reset(captchaRef.current);
        setCaptchaVerified(false);
      }
    };
  
    const setCaptchaRef = useCallback((node) => {
      if (node !== null && !captchaRef.current) {
        captchaRef.current = node;
        window.turnstile.render(node, {
          sitekey: contactConfig.cloud_flare_sitekey,
          callback: handleCaptchaSuccess,
          errorcallback: handleCaptchaError,
        });
      }
    }, [contactConfig.cloud_flare_sitekey, ]);

  const useStyles = makeStyles((theme) => ({
    paper: {
      maxWidth: 1200,
      marginTop: 'theme.spacing(3)',
      margin: `${theme.spacing(1)}px auto`,
      marginBottom: '24px',
      padding: '100px',
      paddingTop: '80px',
      paddingBottom: '80px',
      boxShadow:components.length>0?"0px 20px 40px 0px rgba(107, 114, 128, 0.20)":'',
      borderRadius: "24px",
      border:components.length>0?"1px solid #B6BAC3":'',
      fontSize: '18px',
      textAlign: components.length>0?'':'center',
      [theme.breakpoints.down("1025")]: {
        paddingTop: '40px',
        paddingBottom: '40px',
      },
      [theme.breakpoints.down("lg")]: {
        width: "90%",
      },
      [theme.breakpoints.down("xs")]: {
        width: "90%",
        fontSize: '14px',
        padding: theme.spacing(3),
      },
    },
    formInputControl: {
      gap: '4px',
      display: "flex !important",
      margin: "10px 0 !important",

      "& .MuiFormHelperText-root": {
        fontSize: "14px",
        color: "#212931",
        fontWeight: "700",
        marginLeft: 0,
      },
      [theme.breakpoints.only('xs')]: {
        gap: '2px',
      },
    },
    captchaWidth: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: '100%',
      },
    },
    captchaContainer: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: '-webkit-fill-available',
      },
    },
    captchaError: {
      color: "#D82B0C",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      marginLeft: "12px",
    },
    blockTitle: {
      fontWeight: "800",
      marginBottom: "18px",
      fontSize: "90px",
      textAlign: components.length > 0 ? 'start': 'center',
      [theme.breakpoints.only('xs')]: {
        fontSize: "36px",
      },
    },
    alertBoxsuccess: {
      marginTop: '10px',
      color: " #44a047",
      backgroundColor: "rgba(93, 157, 81, 0.2)",
      border: "1px solid #44a047",
      display: "flex",
      alignItems: "center",
      fontSize: "14px",
      padding: "1.4rem 0.6rem",
      borderRadius: "8px",
      width: "98%",
      margin: "0 auto",
    },
    contactInfo: {
      alignItems: "center",
      display: "flex",
      width: "fit-content",
      margin: "8px 0",
      [theme.breakpoints.only('xs')]: {
        margin: "0",
      },
    },
    avatar: {
      marginTop:'2px',
      marginRight: "16px",
      width: "14px",
      height: "14px",
    },
    contactTag: {
      margin: "0 !important",
      color: "#25272C",
    },
    textField: {
      fontSize: "18px",
      marginBottom: "12px",
      "&>div": {
        fontSize: "18px",
        borderRadius: "10px",
      },
      "& input": {
        padding: '19px'
      },
      // Mobile & mini ipad < 600px
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        "&>div": {
          fontSize: "14px",
        },
        "& input": {
          fontSize: "14px",
          padding: "12px",
        }
      },
    },
    textareaField: {
      overflow: "hidden",
      minHeight: "160px",
      borderRadius: "10px",
      paddingLeft: "12px",
      fontSize: "18px",
      borderColor: "rgba(0, 0, 0, 0.23)",
      padding: '16px',
      '&::placeholder': {
        color: '#a2a2a2',
      },
      // Mobile & mini ipad < 600px
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        fontSize: "14px",
        paddingTop: "8px",
        height: '160px',
        padding: ''
      },
    },
    errortextareafield: {
      width: "29em",
      minHeight: "120px",
      overflow: "hidden",
      background: "#FBEAE7",
      paddingLeft: "12px",
      fontSize: "16px",
      fontWeight: "400",
      color: "#D82B0C",
      fontStyle: "normal",
      borderRadius: "10px",
      borderColor: "#f44336",
      // Mobile & mini ipad < 600px
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    errortextfield: {
      width: "29em",
      background: "#FBEAE7",
      fontSize: "16px",
      "& .MuiFormHelperText-root": {
        fontSize: "14px",
        fontWeight: "400",
        color: "#D82B0C",
        fontStyle: "normal",
        borderRadius: "4px",
      },
      "&>div": {
        fontSize: "16px",
        height: "48px",
        borderRadius: "4px",
      },
      "& input": {
        padding: "14.5px 14px",
      },
      // Mobile & mini ipad < 600px
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    errorMessage: {
      fontSize: "14px !important",
      fontWeight: "400 !important",
      color: "#D82B0C !important",
      fontStyle: "normal !important",
      marginBottom: "5px",
    },
    submitBtn: {
      color: contactConfig?.button_text_color,
      outline: "none",
      marginTop: '4px',
      padding: "16px",
      lineHeight: '110%',
      fontSize: "18px",
      fontWeight: 700,
      fontFamily: "Switzer",
      width: "150px",
      maxWidth: "156px",
      boxShadow: "none",
      borderRadius: "30px",
      backgroundColor: contactConfig?.button_background_color,
      textTransform: "capitalize",
      boxShadow: "0px 2px 4px 0px rgba(107, 114, 128, 0.12), 0px 3px 5px 0px rgba(107, 114, 128, 0.15)",

      "&:hover": {
        boxShadow: "none",
        backgroundColor: contactConfig?.button_background_color,
      },
      "&:focus": {
        boxShadow: "none",
        backgroundColor: contactConfig?.button_background_color,
        outline: "none",
      },
      "&:focus-visible": {
        outline: "none",
      },
      "&.Mui-disabled": {
        padding: "16px 24px",
        width: "180px",
        maxWidth: "186px"
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        maxWidth: "100%",
        padding: "12px 24px",
        fontSize: "14px"
      },
    },
    contact_info_text:{
      textAlign:'left'
    },
    faqLink: {
      color: (props) => props.contactConfig?.button_background_color,
      textDecoration: 'none',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    gridContainer: {
      [theme.breakpoints.up('md')]: {
        '& > .MuiGrid-item:first-child': {
          paddingRight: '32px',
        },
        '& > .MuiGrid-item:last-child': {
          paddingLeft: components.length>0?'32px':'',
        },
      },
    },
    customH6: {
      fontSize: '18px',
      fontWeight: 700,
      textAlign: 'left',
      [theme.breakpoints.down("xs")]: {
        fontSize: '14px',
        margin: '0',
      },
    },
  }));

  const classes = useStyles();

  useEffect(() => {
    // Set user values after login
    const cachedUser = JSON.parse(localStorage.getItem("userData"))
      ? JSON.parse(localStorage.getItem("userData"))
      : user;
    setValues({
      ...values,
      email: cachedUser?.email,
      firstName: cachedUser?.firstName,
      lastName: cachedUser?.lastName
    });
  }, [user]);

  const renderContactInfoText = () => {
    const contactTexts = [];
    if (contactConfig.contact_info_text) {
      contactTexts.push(
        <div
          key="contact-text-1"
          className="contact-text-column"
          dangerouslySetInnerHTML={{ __html: contactConfig.contact_info_text }}
        />
      );
    }
    return contactTexts.map((text, index) => (
      <div key={`contact-text-wrapper-${index}`} className={`contact-text-column-${index + 1}`}>
        {text}
      </div>
    ));
  };

  // input filed values assigning
  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    setErrors({ ...errors, [`${prop}Error`]: "" });
    setValidations({ ...validations, successMessage: "" });
  };

  const handleFormFieldclass = (fieldValue) => {
    if (fieldValue === "messageError") {
      return errors[fieldValue] ? classes.errortextareafield : classes.textareaField;
    }
    
    const field = fieldValue.replace('Error', '');
    const hasError = errors[fieldValue] && values[field] !== "";
    return hasError ? classes.errortextfield : classes.textField;
  };

  const onHandleSubmit = async () => {
    if (window.gtag) {
      window.gtag("event", "customer_care_form_submit", {
        contact_Submit: "Customer Care form submitted",
        captcha_verified: captchaVerified,
      });
    }
    if (!captchaVerified && (contactConfig.auth_visibility === 'unauthenticated')) {
      setCaptchaError('CAPTCHA verification failed. Please try again.');
      resetCaptcha();
      return;
    }
    setValidations({ ...validations, isLoading: true });
    const emailErr = validateEmailAddress(values.email, "forgot");
    const firstNameErr = values.firstName.trim() === "" ? "Field must not be blank" : "";
    const lastNameErr = values.lastName.trim() === "" ? "Field must not be blank" : "";
    const messageErr = validateStandard(values.message);
  
    if (emailErr === "" && firstNameErr === "" && lastNameErr === "" && messageErr === "") {
      
      const lambdaPayload = {
        action: "trigger_customer_care",
        email: values.email,
        retailer: contactConfig?.retailer_name || "",
        first_name: values.firstName,
        last_name: values.lastName,
        shopper_customer_key: contactConfig?.shopper_customer_key || "",
        contact_customer_key: contactConfig?.contact_customer_key || "",
        send_to_shopper: contactConfig?.send_to_shopper,
        content: values.message,
        brandsite_url: contactConfig?.brandsite_url || "",
        customer_care_email: contactConfig?.customer_care_email || "",
        program_id: siteConfig[0].site_type === "LoyaltySync" ? (retailerDpnInstance?.loyalty_program_id || "") : (dpnInstance?.loyalty_program_id || ""),
        member_id: isLogin ? (cachedUser?.username || "") : "",
        client_id: contactConfig?.client_ID,
        dpn_instance_name: siteConfig[0].site_type === "LoyaltySync" ? (retailerDpnInstance?.dpn_instance || "") : (dpnInstance?.dpn_instance || ""),
      };

      try {
        const response = await axios.post(
          process.env.REACT_APP_SFMC_LAMBDA,
          lambdaPayload
        );
        setValidations({ isLoading: false, successMessage: "Your message has been sent successfully." });
      } catch (error) {
        console.error("Error calling Lambda:", error);
        setValidations({ isLoading: false, successMessage: "" });
        setErrors({ ...errors, generalError: "An error occurred while submitting the form. Please try again." });
      }
    } else {
      setErrors({
        emailError: emailErr,
        firstNameError: firstNameErr,
        lastNameError: lastNameErr,
        messageError: messageErr,
      });
      setValidations({ ...validations, isLoading: false });
    }
  };

  if (!contactConfig) {
    return <>Loading...</>;
  }
  return (
    <Paper className={classes.paper} elevation={0}>
      <Grid
        container
        spacing={2}
        style={{  }}
        className={classes.gridContainer}
      >
        <Grid item xs={12} sm={12} md={components.length>0 ? 6:12} lg={components.length>0 ? 6 : 12}>
          <>
            <h1
              tabIndex={0}
              className={classes.blockTitle}
              noWrap
              data-testid="signIn"
              style={{ color: contactConfig.title_color, textAlign:components.length>0?'':'center' }}
            >
              {contactConfig.title}
            </h1>

            {/*  new code added */}
            <div className={classes.contact_info_text}>
              {renderContactInfoText()}
            </div>
            {/* <p style={{ color: "#25272C" }}>
              For questions about retailer registration or your digital rewards,
              please review {contactConfig?.retailer_name ?? client?.name ?? ""}{" "}
              <a
                style={{ color: contactConfig?.background_color }}
                onClick={() => navigate(getFullPath("faq"))}
                className={classes.faqLink}
              >
                FAQs.
              </a>
            </p>
            <p style={{ color: "#25272C" }}>
              For additional {contactConfig?.retailer_name ?? client?.name ?? ""}{" "}
              related questions, please contact:
            </p> */}
              {(contactConfig.customer_care_phone || contactConfig.customer_care2_email) && (
                <>
                  <br></br>
                  <h6 className={classes.customH6}>
                    {contactConfig.customer_care_title_text}
                  </h6>
                </>
              )}
              {(contactConfig.customer_care2_email || contactConfig.customer_care_email) && (
                <>
                  <div className={classes.contactInfo}>
                    <img className={classes.avatar} src={EmailIcon} alt="Email Icon" />
                    <a href={`mailto:${contactConfig.customer_care2_email ? contactConfig.customer_care2_email : contactConfig.customer_care_email }`} className={classes.contactTag}>
                      {contactConfig.customer_care2_email ? contactConfig.customer_care2_email : contactConfig.customer_care_email}
                    </a>
                  </div>
                </>
              )}
              {(contactConfig.customer_care_phone) && (
                <>
                  <div className={classes.contactInfo}>
                    <img className={classes.avatar} src={PhoneIcon} alt="Contact Icon" />
                    <a href={`tel:${contactConfig.customer_care_phone}`} className={classes.contactTag}>
                      {contactConfig.customer_care_phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
                    </a>
                  </div>
                </>
              )}
          </>
        </Grid>
        <Grid item xs={12} sm={12} md={components.length>0 ? 6:12} lg={components.length>0 ? 6:12}>
          <>
            <FormControl
              className={classes.formInputControl}
              variant="outlined"
            >
              <TextField
                id="FirstName"
                variant="outlined"
                error={errors.firstNameError !== "" && values.firstName !== ""}
                value={values.firstName}
                onChange={handleChange("firstName")}
                placeholder="First Name"
                className={handleFormFieldclass("firstNameError")}
              />
              <TextField
                id="LastName"
                variant="outlined"
                error={errors.lastNameError !== "" && values.lastName !== ""}
                value={values.lastName}
                onChange={handleChange("lastName")}
                placeholder="Last Name"
                className={handleFormFieldclass("lastNameError")}
              />
              {(errors.firstNameError !== "" || errors.lastNameError !== "") && (
                <FormHelperText
                  className={classes.errorMessage}
                  id="outlined-weight-helper-text"
                >
                  {errors.firstNameError || errors.lastNameError}
                </FormHelperText>
              )}
              <TextField
                id="Email"
                variant="outlined"
                error={errors.emailError != ""}
                placeholder="Email"
                value={values.email}
                onChange={handleChange("email")}
                className={handleFormFieldclass("emailError")}
              />
              {errors.emailError !== "" && (
                <FormHelperText
                  className={classes.errorMessage}
                  id="outlined-weight-helper-text"
                >
                  {errors.emailError}
                </FormHelperText>
              )}
              <TextareaAutosize
                maxRows={45}
                id="Text"
                variant="outlined"
                error={errors.messageError != ""}
                placeholder="Message"
                value={values.text}
                onChange={handleChange("message")}
                className={handleFormFieldclass("messageError")}
              />
              {errors.messageError !== "" && (
                <FormHelperText
                  className={classes.errorMessage}
                  id="outlined-weight-helper-text"
                >
                  {errors.messageError}
                </FormHelperText>
              )}
            </FormControl>

            {contactConfig.cloud_flare_sitekey && (contactConfig.auth_visibility === 'unauthenticated') && (
              <>
                <div ref={setCaptchaRef} className={`cf-turnstile ${classes.captchaContainer}`} data-sitekey={contactConfig.cloud_flare_sitekey} data-callback={handleCaptchaSuccess} data-theme="light"></div>
                {captchaError && (
                  <Typography className={classes.captchaError}>
                    {captchaError}
                  </Typography>
                )}
              </>
            )}
            
            <Button
              variant="contained"
              onClick={() => onHandleSubmit()}
              disabled={validations.isLoading}
              className={classes.submitBtn}
            >
              {validations.isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CircularProgress size={20} color="inherit" />
                  <span>Sending...</span>
                </div>
              ) : (
                <div data-testid="signBtn" className="signBtn">
                  Submit
                </div>
              )}
            </Button>
            {validations.successMessage !== "" &&
              validations.successMessage !== undefined && (
                <div className={classes.alertBoxsuccess}>
                  Your message has been sent successfully.
                </div>
              )}
          </>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ContactBlock;